import {
  Divider,
  MenuItem,
  SelectChangeEvent,
  Switch,
  Typography,
  Box,
  Grid,
} from '@mui/material';
import React, { useEffect, useState } from 'react';
import { farmService } from '../FarmService';
import { Farm, FarmTopologies } from '../Model/FarmModel';
import { useHistory, useParams } from 'react-router-dom';
import FarmSkeleton from './Skeleton';
import { farmStateMapping } from '../StateMapper/StateMapper';
import { Cancel, Save } from '@mui/icons-material';
import CustomButton, {
  CustomSelect,
  CustomTextField,
} from '../../../../Common/Components/Controls/InputControls';
import {
  CardWrapper,
  FooterContainer,
  PaperWrapper,
} from '../../Common/CommonComponents';
import DynamicInputField from '../../Customer/Components/DynamicInputField';

interface UriParams {
  FarmId: string;
}
interface Inputfiled {
  name: string;
  value: string;
}

const EditForm = () => {
  const initialTopologyState: FarmTopologies[] = [];
  const initialRegionState: string[] = [];
  const initialInputFieldsState: Inputfiled[] = [];
  const history = useHistory();
  const [topologies, setFarmTopologies] = useState(initialTopologyState);
  const [regions, setFarmRegions] = useState(initialRegionState);
  const [inputFields, setInputFields] = useState(initialInputFieldsState);
  const [newinputFields, setNewInputfields] = useState([
    { name: '', value: '' },
  ]);
  const params = useParams<UriParams>();
  const [formData, setFormData] = useState({
    Id: 0,
    DisplayName: '',
    GroupName: '',
    Location: '',
    Type: '',
    State: 7,
    DeploymentState: -1,
    SetupFilename: '',
    FarmRegion: 'northeurope',
    SecondaryRegion: 'westeurope',
    SubState: '',
    Properties: '',
    Subscription: '',
    IsHighAvailability: false,
  });

  useEffect(() => {
    loadFarmData().catch((error) => console.log(error));
    loadFarmTopologiesData().catch((error) => console.log(error));
    loadFarmRegionsData().catch((error) => console.log(error));
  }, []);

  const loadFarmData = async () => {
    const farmReceived = await farmService.getFarm(+params.FarmId);
    setFormData(farmReceived);

    if (farmReceived.Properties) {
      const properties: { [key: string]: string } = JSON.parse(
        farmReceived.Properties,
      );
      const keys = Object.keys(properties);

      for (const element of keys) {
        inputFields.push({ name: element, value: properties[element] });
      }
    }
  };

  const loadFarmTopologiesData = async () => {
    const farmsTopologiesReceived = await farmService.getFarmTopologies();
    setFarmTopologies(farmsTopologiesReceived.Items);
  };
  const loadFarmRegionsData = async () => {
    const farmsRegionsReceived = await farmService.getFarmRegions();
    setFarmRegions(farmsRegionsReceived);
  };
  const getLocationAndType = () => {
    const topo = topologies.find((x) => x.FarmType === Type);
    return `${topo?.DisplayName} @ ${topo?.FarmType}`;
  };

  const handleChangeInputValue = (
    index: number,
    event: React.ChangeEvent<HTMLTextAreaElement | HTMLInputElement>,
  ) => {
    const updatedValue = event.target.value;
    const fieldName = event.target.name as keyof (typeof inputFields)[0];

    // Use the appropriate input fields based on the condition
    const values =
      inputFields.length < 1 ? [...newinputFields] : [...inputFields];

    // Update the specific field
    values[index][fieldName] = updatedValue;

    // Set the state based on the condition
    if (inputFields.length < 1) {
      setNewInputfields(values);
    } else {
      setInputFields(values);
    }
  };

  const handleSubmit = (e: { preventDefault: () => void }) => {
    e.preventDefault();
  };

  const handleAddField = () => {
    if (inputFields.length < 1) {
      setNewInputfields([...newinputFields, { name: '', value: '' }]);
    } else {
      setInputFields([...inputFields, { name: '', value: '' }]);
    }
  };

  const handleRemoveField = (index: number) => {
    if (inputFields.length < 1) {
      const values = [...newinputFields];
      values.splice(index, 1);
      setInputFields(values);
    } else {
      const values = [...inputFields];
      values.splice(index, 1);
      setInputFields(values);
    }
  };

  const handleClose = () => {
    history.push('/farm');
  };

  const handleSave = async () => {
    const properties: { [key: string]: string } = {};
    if (inputFields.length < 1) {
      for (const element of newinputFields) {
        const prop = element;
        if (!prop.name) continue;

        properties[prop.name] = prop.value;
      }
    } else {
      for (const element of inputFields) {
        const prop = element;
        if (!prop.name) continue;

        properties[prop.name] = prop.value;
      }
    }
    const propertyString = JSON.stringify(properties);

    formData.Properties = propertyString;
    await farmService.postFarm(formData.Id, formData as Farm);
    history.push('/farm');
  };
  const handleValueChange = (event: SelectChangeEvent<string>) => {
    const { name, value, checked } = event.target as HTMLInputElement;
    setFormData({
      ...formData,
      [name]: checked !== undefined ? checked : value.trim(),
    });
  };

  const updateFarmData = (
    event: React.ChangeEvent<HTMLTextAreaElement | HTMLInputElement>,
  ) =>
    setFormData({
      ...formData,
      [event.target.name]: event.target.value,
    });

  const {
    DisplayName,
    GroupName,
    State,
    Location,
    Type,
    SetupFilename,
    FarmRegion,
    SecondaryRegion,
    IsHighAvailability,
  } = formData;

  return (
    <div>
      <PaperWrapper>
        <Typography variant="h5" sx={{ mb: 2 }}>
          Edit Farm: {params.FarmId}
        </Typography>
        <Divider sx={{ mb: 3 }} />
        <Grid container spacing={3}>
          {Location ? (
            <>
              <Grid item xs={12} sm={12} md={6} lg={6}>
                <CardWrapper>
                  <Typography variant="h6" sx={{ mb: 2, fontWeight: 500 }}>
                    Basic Informations
                  </Typography>
                  <Divider sx={{ mb: 2 }} />
                  <Grid container spacing={2} alignItems="center" mt={2}>
                    <Grid item xs={12} sm={6}>
                      <Typography variant="body1">Display Name</Typography>
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <CustomTextField
                        value={DisplayName}
                        onChange={(e) => updateFarmData(e)}
                        type="text"
                        name="DisplayName"
                        required
                      />
                    </Grid>
                  </Grid>
                  <Grid container spacing={2} alignItems="center" mt={2}>
                    <Grid item xs={12} sm={6}>
                      <Typography variant="body1">Group Name</Typography>
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <CustomTextField
                        disabled={true}
                        value={GroupName}
                        type="text"
                        name="DisplayName"
                        required
                      />
                    </Grid>
                  </Grid>
                  <Grid container spacing={2} alignItems="center" mt={2}>
                    <Grid item xs={12} sm={6}>
                      <Typography variant="body1">State</Typography>
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <CustomSelect
                        value={State.toString()}
                        onChange={(e) => handleValueChange(e)}
                        type="text"
                        name="State"
                        required
                      >
                        {Object.entries(farmStateMapping).map((StateArray) => {
                          return (
                            <MenuItem key={StateArray[0]} value={StateArray[0]}>
                              {StateArray[1]}
                            </MenuItem>
                          );
                        })}
                      </CustomSelect>
                    </Grid>
                  </Grid>
                  <Grid container spacing={2} alignItems="center" mt={2}>
                    <Grid item xs={12} sm={6}>
                      <Typography variant="body1">
                        Location & Farm Type
                      </Typography>
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <CustomTextField
                        disabled={true}
                        value={getLocationAndType()}
                        type="text"
                        name="Location"
                        required
                      />
                    </Grid>
                  </Grid>
                  <Grid container spacing={2} alignItems="center" mt={2}>
                    <Grid item xs={12} sm={6}>
                      <Typography variant="body1">Assemble Build</Typography>
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <CustomTextField
                        disabled={true}
                        value={SetupFilename}
                        type="text"
                        name="Location"
                        required
                      />
                    </Grid>
                  </Grid>
                  <Grid container spacing={2} alignItems="center" mt={2}>
                    <Grid item xs={12} sm={6}>
                      <Typography variant="body1">Farm Region</Typography>
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <CustomSelect
                        value={FarmRegion}
                        onChange={(e) => handleValueChange(e)}
                        type="text"
                        name="FarmRegion"
                        required
                      >
                        {regions.map((region) => {
                          return (
                            <MenuItem key={region} value={region}>
                              {region}
                            </MenuItem>
                          );
                        })}
                      </CustomSelect>
                    </Grid>
                  </Grid>
                  <Grid container spacing={2} alignItems="center" mt={2}>
                    <Grid item xs={12} sm={6}>
                      <Typography variant="body1">Secondary Region</Typography>
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <CustomSelect
                        value={SecondaryRegion}
                        onChange={(e) => handleValueChange(e)}
                        type="text"
                        name="SecondaryRegion"
                        required
                      >
                        {regions.map((region) => {
                          return (
                            <MenuItem key={region} value={region}>
                              {region}
                            </MenuItem>
                          );
                        })}
                      </CustomSelect>
                    </Grid>
                  </Grid>
                  <Grid container spacing={2} alignItems="center" mt={2}>
                    <Grid item xs={12} sm={6}>
                      <Typography variant="body1">High Availability</Typography>
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <Switch
                        checked={IsHighAvailability}
                        onChange={(e) => handleValueChange(e)}
                        name="IsHighAvailability"
                      />
                    </Grid>
                  </Grid>
                </CardWrapper>
              </Grid>
              <Grid item xs={12} sm={12} md={6} lg={6}>
                <CardWrapper>
                  <Typography variant="h6" sx={{ mb: 2, fontWeight: 500 }}>
                    Farm Properties
                  </Typography>
                  <Divider sx={{ mb: 2 }} />

                  {inputFields.length < 1 ? (
                    <Box>
                      <form onSubmit={handleSubmit}>
                        {newinputFields.map((inputField, index) => (
                          <DynamicInputField
                            key={index}
                            index={index}
                            inputField={inputField}
                            handleChangeInput={handleChangeInputValue}
                            handleRemoveFields={handleRemoveField}
                            handleAddFields={handleAddField}
                          />
                        ))}
                      </form>
                    </Box>
                  ) : (
                    <Box>
                      <form onSubmit={handleSubmit}>
                        {inputFields.map((inputField, index) => (
                          <DynamicInputField
                            key={index}
                            index={index}
                            inputField={inputField}
                            handleChangeInput={handleChangeInputValue}
                            handleRemoveFields={handleRemoveField}
                            handleAddFields={handleAddField}
                          />
                        ))}
                      </form>
                    </Box>
                  )}
                </CardWrapper>
              </Grid>
            </>
          ) : (
            <FarmSkeleton />
          )}
        </Grid>
        <>
          <Divider sx={{ my: 2 }} />
          <Box sx={{ display: 'flex', justifyContent: 'center' }}>
            <FooterContainer>
              <Grid item xs={12} sm="auto">
                <CustomButton
                  onClick={handleSave}
                  startIcon={<Save />}
                  label={'Save'}
                />
              </Grid>
              <Grid item xs={12} sm="auto">
                <CustomButton
                  onClick={handleClose}
                  startIcon={<Cancel />}
                  label={'Cancel'}
                />
              </Grid>
            </FooterContainer>
          </Box>
        </>
      </PaperWrapper>
    </div>
  );
};

export default EditForm;
